import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, ClipboardList, Inbox, MessageSquare, LogOut, ShieldCheck, ExternalLink, Receipt, DollarSign, Megaphone, UserCircle, BookOpen, Gauge, CalendarDays } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function StaffLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/staff/login");
  };

  const navItems = [
    { to: "/staff", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/staff/clients", label: "Clients", icon: Users },
    { to: "/staff/billing", label: "Billing", icon: Receipt },
    { to: "/staff/utilisation", label: "Utilisation", icon: Gauge },
    { to: "/staff/referrals", label: "Referrals", icon: Inbox },
    { to: "/staff/messages", label: "Messages", icon: MessageSquare },
    { to: "/staff/noticeboard", label: "Noticeboard", icon: Megaphone },
    { to: "/staff/blog", label: "Blog", icon: BookOpen },
    { to: "/staff/events", label: "Events", icon: CalendarDays },
    ...(isAdmin ? [
      { to: "/staff/team", label: "Team", icon: ShieldCheck },
      { to: "/staff/rates", label: "NDIS rates", icon: DollarSign },
    ] : []),
    { to: "/staff/profile", label: "My profile", icon: UserCircle },
  ];

  return (
    <div className="min-h-screen bg-[#F1E4D5]" data-testid="staff-layout">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-[#1A363D] text-[#F0E5DE] sticky top-0">
          <div className="px-6 py-7 border-b border-white/10">
            <div className="font-display text-2xl text-white tracking-tight">NAVICA</div>
            <div className="text-[10px] tracking-[0.22em] italic text-[#F4C3CC] uppercase">CRM</div>
          </div>

          <nav className="flex-1 px-3 py-6 space-y-1">
            {navItems.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition ${
                    isActive ? "bg-[#E2718A] text-white" : "text-[#C9D2D5] hover:bg-white/5 hover:text-white"
                  }`
                }
                data-testid={`staff-nav-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="px-3 pb-6 space-y-2">
            <Link to="/" target="_blank" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#C9D2D5] hover:bg-white/5 hover:text-white transition">
              <ExternalLink className="w-4 h-4" /> Public site
            </Link>
            <div className="px-4 py-3 bg-white/5 rounded-xl">
              <div className="text-xs text-[#9CA9AD] mb-0.5">Signed in as</div>
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#F4C3CC] mt-1">{user?.role}</div>
            </div>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#C9D2D5] hover:bg-[#E2718A] hover:text-white transition" data-testid="staff-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 inset-x-0 bg-[#1A363D] text-white z-40 flex items-center justify-between px-5 h-14">
          <div>
            <div className="font-display text-lg tracking-tight">NAVICA</div>
            <div className="text-[9px] tracking-[0.22em] italic text-[#F4C3CC] uppercase -mt-1">CRM</div>
          </div>
          <button onClick={handleLogout} className="text-sm flex items-center gap-1.5">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#F0E5DE] z-40 flex overflow-x-auto">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex-1 min-w-[70px] flex flex-col items-center justify-center py-2.5 text-[10px] gap-1 ${
                  isActive ? "text-[#E2718A]" : "text-[#6B7A80]"
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Main content */}
        <main className="flex-1 min-w-0 pt-14 pb-20 lg:pt-0 lg:pb-0">
          <div className="p-5 lg:p-10 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
